import React from 'react';
import { Container, Typography, Box, Paper, Button, Grid, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

const About: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: (theme) =>
            `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
          borderBottom: 1,
          borderColor: 'divider',
          py: 8,
          px: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            color="primary"
            sx={{
              fontWeight: 600,
              letterSpacing: 3,
              textTransform: 'uppercase',
              fontSize: '11px',
            }}
          >
            {t('about.label')}
          </Typography>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              my: 2,
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '3.75rem' },
              letterSpacing: '1px',
            }}
          >
            {t('about.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 300, maxWidth: '560px' }}>
            {t('about.subtitle')}
          </Typography>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
          {t('about.whoWeAre')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips is a free video streaming platform designed specifically for Indian audiences. We believe great
          entertainment should be accessible to everyone — regardless of their financial background, location, or
          device.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          Our platform hosts over 32,000 videos across Movies, Web Series, Short Films, Comedy, Music Videos, News,
          Education, Sports and more. With 113,498+ total media files and new content added daily, there is always
          something new to discover.
        </Typography>

        <Divider sx={{ my: 5 }} />

        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
          {t('about.mission')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          To democratize video entertainment in India by providing a free, high-quality streaming experience that
          supports both viewers and content creators.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85 }}>
          We empower independent Indian creators to reach audiences they never could before — without the barriers of
          expensive distribution or production budgets.
        </Typography>

        <Divider sx={{ my: 5 }} />

        <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
          {t('about.whatWeOffer')}
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                🎬 32,000+ Videos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A massive and growing library of Indian video content across all genres and languages.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                📱 Mobile First
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Available on Android and iOS, optimised for Indian internet speeds and devices.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                💯 100% Free
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No subscription, no paywall, no hidden charges. Watch everything for free forever.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                🇮🇳 Made in India
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Built by an Indian team, for Indian audiences, with Indian content at the heart.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                🎭 All Categories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Movies, Web Series, Comedy, Music, News, Education, Sports and much more.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'border-color 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                👤 Creator Platform
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload your content, grow your audience and earn revenue as a creator on IndeeClips.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
          {t('about.numbers')}
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
                2,000+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Users watching videos every day on our platform.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
                113,498+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total media files including videos and images in our content library.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
                3.9M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly ad requests showing strong user engagement and watch time.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 5 }} />

        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
          {t('about.downloadApp')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          IndeeClips is available free on Android and iOS. Download today and start watching thousands of videos
          instantly.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            href="https://play.google.com/store/apps/details?id=com.ind.indeeclips"
            target="_blank"
            rel="noreferrer"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            ▶ Download on Android
          </Button>
          <Button
            variant="outlined"
            size="large"
            href="https://apps.apple.com/in/app/indeeclips/id6748682495"
            target="_blank"
            rel="noreferrer"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            📱 Download on iOS
          </Button>
        </Box>

        <Divider sx={{ my: 5 }} />

        <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
          Contact Us
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, lineHeight: 1.85 }}>
          Have questions or want to partner with us? We would love to hear from you.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Email:{' '}
          <Typography
            component="a"
            href="mailto:contact@indeeclips.com"
            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            contact@indeeclips.com
          </Typography>
        </Typography>
      </Container>
    </Box>
  );
};

export default About;
